'use client';

import type {
  PropsWithChildren} from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import type { SendTransactionData } from '@wagmi/core/src/query/sendTransaction';

import { randomBytes } from 'crypto';
import { capitalize } from 'lodash';
import type { Address } from 'viem';
import { useAccount, useSendTransaction, useSignTypedData } from 'wagmi';
import type { StoreApi} from 'zustand';
import { createStore, useStore } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import { useSingleChainSwapTokensMutation } from '@shared/hooks/query/magicSwap';

import type {
  OneInchCrossChainSwapOrderOperation} from '@api/magicSwap';
import {
  buildOneInchCrossChainSwapOrder,
  checkOneInchCrossChainSwapPendingSecrets,
  getOneInchCrossChainSwapOrderStatus,
  submitOneInchCrossChainSwapOrder,
  submitOneInchCrossChainSwapPendingSecret,
} from '@api/magicSwap';
import type { OneInchCrossChainQuote, OneInchNetwork, OneInchQuote, OneInchToken } from '@api/types';

import { HashLock, OrderStatus } from '@1inch/cross-chain-sdk';

import type { SwapOperationSpecification} from '../types';
import { SwapOperationStatus, SwapOperationType } from '../types';

export interface SwapOperationBase {
  id: string;
  type: SwapOperationType;
  status: SwapOperationStatus;
}

export interface UnknownSwapOperationStatusDetails {
  status: SwapOperationStatus.Unknown;
}

export interface PendingSwapOperationStatusDetails {
  status: SwapOperationStatus.Pending;
}

export interface FailedSwapOperationdDetails {
  status: SwapOperationStatus.Failed;
  error: string;
}

export interface CompletedSwapOperationStatusDetails {
  status: SwapOperationStatus.Completed;
}

export type SwapOperationStatusDetails =
  | UnknownSwapOperationStatusDetails
  | PendingSwapOperationStatusDetails
  | FailedSwapOperationdDetails
  | CompletedSwapOperationStatusDetails;

export interface SingleChainSwapOperationDetails {
  type: SwapOperationType.SingleChain;
  hash?: Address;
}

export interface CrossChainSwapOperationDetails
  extends Omit<OneInchCrossChainSwapOrderOperation, 'operationId'> {
  type: SwapOperationType.CrossChain;
}

export type SwapOperation = SwapOperationBase &
  SwapOperationStatusDetails &
  (SingleChainSwapOperationDetails | CrossChainSwapOperationDetails);

export type CrossChainSwapOperation = SwapOperation & CrossChainSwapOperationDetails;

export type SingleChainSwapOperation = SwapOperation & SingleChainSwapOperationDetails;

interface SwapOperationExecutorStore {
  operations: Array<SwapOperation>;

  initiateSwapOperation(
    specification: SwapOperationSpecification,
    quote: OneInchQuote,
  ): Promise<SwapOperation>;
  markSwapOperationAsCompleted(id: string): void;
  markSwapOperationAsFailed(id: string, error: string): void;
  purgeSwapOperation(id: string): void;
}

const SwapOperationExecutorContext = createContext<StoreApi<SwapOperationExecutorStore> | null>(
  null,
);

const SWAP_MONITORING_INTERVAL_MS = 5000;

const provideCrossChainSwapOperationSecrets = async (
  operation: CrossChainSwapOperation,
): Promise<void> => {
  const { fills = [] } = await checkOneInchCrossChainSwapPendingSecrets(operation.order);

  const promises = fills.map(({ idx }) => {
    return submitOneInchCrossChainSwapPendingSecret({
      orderHash: operation.order.orderHash,
      secret: operation.secrets[idx].value,
    });
  });

  const results = await Promise.allSettled(promises);

  results.forEach((result, index) => {
    if (result.status === 'rejected') {
      console.error(`Failed to submit secret for fill ${index}: ${result.reason}`);
    }
  });
};

const processCrossChainSwapOperation = async (
  operation: CrossChainSwapOperation,
): Promise<SwapOperationStatusDetails> => {
  const oneInchOrderStatus = await getOneInchCrossChainSwapOrderStatus(operation.order.orderHash);

  switch (oneInchOrderStatus) {
    case OrderStatus.Pending:
      await provideCrossChainSwapOperationSecrets(operation);

      return {
        status: SwapOperationStatus.Pending,
      };

    case OrderStatus.Executed:
      return {
        status: SwapOperationStatus.Completed,
      };

    case OrderStatus.Expired:
    case OrderStatus.Cancelled:
    case OrderStatus.Refunding:
    case OrderStatus.Refunded:
      return {
        status: SwapOperationStatus.Failed,
        error: 'Cross-chain swap operation failed.',
      };

    default:
      console.error(`Unknown order status: ${oneInchOrderStatus}`);

      return {
        status: SwapOperationStatus.Unknown,
      };
  }
};

const SwapOperationsMonitor = () => {
  const { operations, markSwapOperationAsCompleted, markSwapOperationAsFailed } =
    useSwapOperationExecutor();

  const processOperations = useCallback(() => {
    operations.map(async (operation) => {
      switch (operation.type) {
        case SwapOperationType.SingleChain:
          console.log(
            `SwapOperationsMonitor: operation with id ${operation.id} is single-chain, nothing to do.`,
          );
          break;

        case SwapOperationType.CrossChain:
          // eslint-disable-next-line no-case-declarations
          const result = await processCrossChainSwapOperation(operation);

          if (result.status === SwapOperationStatus.Completed) {
            console.log(
              `SwapOperationsMonitor: operation with id ${operation.id} has been completed.`,
            );

            markSwapOperationAsCompleted(operation.id);
          } else {
            if (result.status === SwapOperationStatus.Failed) {
              console.error(`SwapOperationsMonitor: operation with id ${operation.id} has failed.`);

              markSwapOperationAsFailed(operation.id, result.error);
            }
          }

          break;
      }
    });
  }, [markSwapOperationAsCompleted, markSwapOperationAsFailed, operations]);

  useEffect(() => {
    if (operations.length > 0) {
      const timer = window?.setInterval(processOperations, SWAP_MONITORING_INTERVAL_MS);

      return () => {
        window?.clearInterval(timer);
      };
    }
  }, [operations, processOperations]);

  return false;
};

interface OneInchCrossChainSecret {
  value: string;
  hash: string;
}

const generateOneInchCrossChainSwapSecrets = (count: number): Array<OneInchCrossChainSecret> => {
  return Array.from({
    length: count,
  }).map(() => {
    const value = '0x' + randomBytes(32).toString('hex');
    const hash = HashLock.hashSecret(value);

    return {
      value,
      hash,
    };
  });
};

const SwapOperationExecutorProvider = ({ children }: PropsWithChildren) => {
  const { address } = useAccount();
  const { signTypedDataAsync } = useSignTypedData();

  const { sendTransactionAsync } = useSendTransaction();

  const { mutateAsync: swapTokens } = useSingleChainSwapTokensMutation();

  const initiateSingleChainSwapOperation = async (
    network: OneInchNetwork,
    sourceToken: OneInchToken,
    destinationToken: OneInchToken,
    walletAddress: Address,
    amount: bigint,
    splippagePercent: number,
  ): Promise<SingleChainSwapOperation> => {
    try {
      const response = await swapTokens({
        networkId: network.id,
        options: {
          searchParams: {
            fromTokenAddress: sourceToken.id,
            toTokenAddress: destinationToken.id,
            amount: amount.toString(),
            walletAddress,
            slippage: splippagePercent,
          },
        },
      });

      if ('error' in response) {
        throw new Error(capitalize(response?.message || response.error?.message || ''));
      }

      const operationId = response.id;

      const hash = (await Promise.race([
        sendTransactionAsync({
          ...(response.data.tx as any),
        }),
      ])) as Awaited<SendTransactionData>;

      return {
        type: SwapOperationType.SingleChain,
        id: operationId,
        hash,
        status: SwapOperationStatus.Pending,
      };
    } catch (error) {
      console.error(error);

      throw error;
    }
  };

  const initiateCrossChainSwapOperation = async (
    sourceNetwork: OneInchNetwork,
    sourceToken: OneInchToken,
    destinationNetwork: OneInchNetwork,
    destinationToken: OneInchToken,
    walletAddress: Address,
    amount: bigint,
    quote: OneInchCrossChainQuote,
  ): Promise<CrossChainSwapOperation> => {
    console.log('initiateCrossChainSwapOperation', {
      sourceNetwork,
      sourceToken,
      destinationNetwork,
      destinationToken,
      walletAddress,
      amount,
      quote,
    });

    const presetVariant = quote.recommendedPreset || 'medium';

    const preset = quote.presets[presetVariant];

    if (!preset) {
      throw new Error(`Preset '${presetVariant}' is not present in the provided quote.`);
    }

    const secrets = generateOneInchCrossChainSwapSecrets(preset.secretsCount);

    const { newOrder: order, operationId } = await buildOneInchCrossChainSwapOrder({
      srcChain: sourceNetwork.id,
      dstChain: destinationNetwork.id,
      srcTokenAddress: sourceToken.id,
      dstTokenAddress: destinationToken.id,
      walletAddress,
      amount: amount.toString(),
      quote,
      preset: presetVariant,
      secretsHashList: secrets.map(({ hash }) => hash),
    });

    const signature = await signTypedDataAsync(order.typedData);

    await submitOneInchCrossChainSwapOrder({
      order,
      walletAddress,
      operationId,
      extension: order.extension,
      quoteId: quote.quoteId,
      signature,
      srcChainId: sourceNetwork.id,
    });

    return {
      id: operationId,
      type: SwapOperationType.CrossChain,
      status: SwapOperationStatus.Pending,
      order,
      secrets,
    };
  };

  const [store] = useState(() => {
    return createStore<SwapOperationExecutorStore>()(
      devtools(
        persist(
          (set, get) => ({
            operations: [],

            initiateSwapOperation: async (
              { source, destination, slippagePercent }: SwapOperationSpecification,
              quote: OneInchQuote,
            ) => {
              console.log('initiateSwapOperation', { source, destination, slippagePercent, quote });

              try {
                if (!address) {
                  throw new Error('Web3 wallet was not found.');
                }

                if (!source.token) {
                  throw new Error('Source token not set.');
                }

                if (!destination.token) {
                  throw new Error('Destination token not set.');
                }

                let operation: SwapOperation;

                if (source.network.id === destination.network.id && !quote.details) {
                  operation = await initiateSingleChainSwapOperation(
                    source.network,
                    source.token,
                    destination.token,
                    address,
                    source.amount,
                    slippagePercent,
                  );
                } else {
                  if (source.network.id !== destination.network.id && quote.details) {
                    operation = await initiateCrossChainSwapOperation(
                      source.network,
                      source.token,
                      destination.network,
                      destination.token,
                      address,
                      source.amount,
                      quote.details,
                    );
                  } else {
                    throw new Error(
                      `Quote type does not match the source and destination networks.`,
                    );
                  }
                }

                const { operations: existingOperations } = get();

                set({ operations: [...existingOperations, operation] });

                return operation;
              } catch (error) {
                console.error(error);

                throw error;
              }
            },
            markSwapOperationAsCompleted: (id: string): void => {
              const { operations } = get();

              console.log('markSwapOperationAsCompleted');

              set({
                operations: operations.map((operation) => {
                  if (operation.id === id) {
                    console.log(
                      'markSwapOperationAsCompleted: operation marked as complete',
                      operation,
                    );

                    return {
                      ...operation,
                      status: SwapOperationStatus.Completed,
                    };
                  }

                  return operation;
                }),
              });
            },
            markSwapOperationAsFailed: (id: string, error: string): void => {
              const { operations } = get();

              set({
                operations: operations.map((operation) => {
                  if (operation.id === id) {
                    return {
                      ...operation,
                      status: SwapOperationStatus.Failed,
                      error,
                    };
                  }

                  return operation;
                }),
              });
            },
            purgeSwapOperation: (id: string): void => {
              console.log('purgeSwapOperation', id);
              const { operations } = get();

              set({
                operations: operations.filter((operation) => operation.id !== id),
              });
            },
          }),
          {
            name: 'SWAP_TRANSACTION_EXECUTOR',
          },
        ),
      ),
    );
  });

  return (
    <SwapOperationExecutorContext.Provider value={store}>
      {children}
      <SwapOperationsMonitor />
    </SwapOperationExecutorContext.Provider>
  );
};

const useSwapOperationExecutor = () => {
  const store = useContext(SwapOperationExecutorContext);

  if (!store) {
    throw new Error(`SwapOperationExecutorContext is not available.`);
  }

  return useStore(store);
};

export { SwapOperationExecutorProvider, useSwapOperationExecutor, type SwapOperationExecutorStore };
